import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const UPDATED = '23 September 2026';

const Section = ({ id, title, children }) => (
  <section id={id} className="mt-10">
    <h2 className="text-lg font-bold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-base text-slate-700 leading-relaxed">{children}</div>
  </section>
);

const PrivacyPage = () => (
  <div className="min-h-screen bg-white" data-testid="privacy-page">
    <SEO
      title="Privacy Policy | Paisley.org.uk"
      description="How Paisley.org.uk handles your personal data: what we collect, why, who we share it with, how long we keep it, and your rights under UK GDPR."
      url="/privacy"
    />
    <Header />
    <main className="max-w-3xl mx-auto px-6 py-16 md:py-24">
      <p className="text-xs uppercase tracking-[0.3em] font-bold text-cyan-700 mb-3">Legal</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
        Privacy Policy
      </h1>
      <p className="mt-4 text-sm text-slate-500">Last updated {UPDATED}</p>

      <p className="mt-8 text-base text-slate-700 leading-relaxed">
        This policy explains what Paisley.org.uk does with personal data. It describes what the site
        actually collects rather than every eventuality: if something here does not match your
        experience of the site, tell us and we will correct it.
      </p>

      <Section id="who-we-are" title="Who we are">
        <p>
          Paisley.org.uk is a community news and information site for Paisley, Renfrewshire,
          operated from 14 High Street, Paisley, Renfrewshire PA1 2BS, Scotland. We are the data
          controller for the information described below. For anything to do with your data,
          contact <a className="text-cyan-700 underline" href="mailto:brian@paisley.org.uk">brian@paisley.org.uk</a>{' '}
          or call 07957 166339.
        </p>
      </Section>

      <Section id="what-we-collect" title="What we collect, and why">
        <p><strong>When you use the contact form.</strong> Your name, email address, subject and
        message. We store the message so we can answer it and keep a record of the conversation.</p>
        <p><strong>When you subscribe to the newsletter.</strong> Your email address, so we can send
        the newsletter. Every email has an unsubscribe link and unsubscribing is immediate.</p>
        <p><strong>When you enquire about a property.</strong> Your name, email, phone number if you
        give one, and your message. This is passed to the estate agent marketing that property so
        they can reply to you — that is the whole point of the form.</p>
        <p><strong>When you submit an event.</strong> Your name and email, so we can verify the
        listing is genuine and come back to you if a detail is unclear.</p>
        <p><strong>When you subscribe to Incident Watch.</strong> You sign in with Google, which
        gives us your name and email address. Payment is handled by Stripe — card details go
        straight to Stripe and never reach our servers. We store your subscription status so we know
        what you are entitled to, and your email so we can send the alerts you paid for.</p>
        <p><strong>Automatically, as you browse.</strong> Standard web analytics (see Cookies below)
        and, for security, the IP addresses of requests to the admin login and to known
        vulnerability-scanning paths — used only to rate-limit and block attacks.</p>
        <p>We do not buy personal data, we do not sell or rent it, and we do not build advertising
        profiles about you.</p>
      </Section>

      <Section id="lawful-basis" title="Our lawful basis">
        <p>
          Contact, event submissions and property enquiries are handled on the basis of{' '}
          <strong>legitimate interests</strong> — you contacted us and expect a reply. The newsletter
          and Incident Watch alert emails are sent with your <strong>consent</strong>, which you can
          withdraw at any time. Incident Watch subscriptions are also processed to{' '}
          <strong>perform our contract</strong> with you. Security logging rests on our legitimate
          interest in keeping the site available.
        </p>
      </Section>

      <Section id="who-we-share-with" title="Who else processes your data">
        <p>We use a small number of established suppliers. They act on our instructions and are not
        permitted to use your data for their own purposes:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Resend</strong> — sends our email (replies, newsletter, Incident Watch alerts).</li>
          <li><strong>Stripe</strong> — takes and manages Incident Watch subscription payments.</li>
          <li><strong>Google</strong> — sign-in for Incident Watch, and Google Analytics.</li>
          <li><strong>Cloudflare</strong> and our hosting provider — serve the site and filter attacks.</li>
          <li><strong>Estate agents</strong> — receive the property enquiry you asked us to pass on.</li>
        </ul>
        <p>We will also disclose information if the law requires it. Some of these suppliers operate
        outside the UK; where they do, transfers rely on the UK&apos;s adequacy regulations or
        standard contractual clauses.</p>
      </Section>

      <Section id="cookies" title="Cookies and analytics">
        <p>
          We use Google Analytics 4 to count visits and see which articles are read. It sets cookies
          in your browser and records a shortened version of your IP address. We do not use it to
          identify you personally, and we do not run advertising or retargeting cookies.
        </p>
        <p>
          <strong>Analytics only runs if you allow it.</strong> The notice you see on your first
          visit starts with analytics switched off, and nothing is measured unless you choose
          &quot;Allow analytics&quot;. You can change your mind at any time using the{' '}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('open-cookie-notice'))}
            className="text-cyan-700 underline"
            data-testid="privacy-cookie-settings"
          >
            Cookie Settings
          </button>{' '}
          link, which also sits in the footer of every page.
        </p>
        <p>
          If you sign in to Incident Watch, a session cookie keeps you signed in. Admin users have a
          login token stored in their own browser. Nothing else is stored on your device.
        </p>
        <p>
          You can block or delete cookies in your browser settings, or install Google&apos;s{' '}
          <a className="text-cyan-700 underline" href="https://tools.google.com/dlpage/gaoptout"
             target="_blank" rel="noopener noreferrer">opt-out add-on</a>. The site works normally
          without them, apart from staying signed in.
        </p>
      </Section>

      <Section id="affiliates" title="Affiliate and sponsored links">
        <p>
          Some links — accommodation, tickets and a few product recommendations — are affiliate
          links, meaning we may earn a commission if you book or buy. Those are marked, and the
          partner&apos;s own privacy policy applies once you leave our site. Commission never changes
          what we choose to cover.
        </p>
      </Section>

      <Section id="retention" title="How long we keep things">
        <p>
          Contact messages and event submissions are kept for two years, then deleted. Newsletter
          subscriptions are kept until you unsubscribe. Property enquiries are kept for two years so
          we can evidence the leads we sent an agent. Incident Watch account and billing records are
          kept for as long as you subscribe and then for six years, because HMRC requires us to keep
          records of payments. Security logs are kept for 30 days.
        </p>
      </Section>

      <Section id="your-rights" title="Your rights">
        <p>
          Under UK GDPR you can ask for a copy of the data we hold about you, ask us to correct it,
          ask us to delete it, object to how we use it, or ask us to restrict its use. Email{' '}
          <a className="text-cyan-700 underline" href="mailto:brian@paisley.org.uk">brian@paisley.org.uk</a>{' '}
          and we will respond within one month. There is no charge.
        </p>
        <p>
          If you are unhappy with how we have handled your data you can complain to the Information
          Commissioner&apos;s Office at{' '}
          <a className="text-cyan-700 underline" href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>{' '}
          or on 0303 123 1113. We would rather you told us first so we can put it right.
        </p>
      </Section>

      <Section id="children" title="Children">
        <p>
          The site is written for a general audience and is not aimed at children. We do not
          knowingly collect data from anyone under 13. If you believe a child has sent us personal
          information, contact us and we will delete it.
        </p>
      </Section>

      <Section id="changes" title="Changes to this policy">
        <p>
          If we change how we handle personal data we will update this page and the date at the top.
          Material changes affecting newsletter or Incident Watch subscribers will be sent by email.
        </p>
      </Section>

      <p className="mt-12 text-sm text-slate-500">
        See also our <Link to="/terms" className="text-cyan-700 underline">Terms of Service</Link>.
      </p>
    </main>
    <Footer />
  </div>
);

export default PrivacyPage;
